import {
  Anchor,
  Box,
  Button,
  Container,
  Grid,
  Group,
  Image,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  img123Final2,
  imgBarCodeFinal2,
  imgDinLabelLaptop,
  imgDinLabelLogoLetras,
  imgImagesFinal,
  imgMailIcon,
  imgQrCodeNuevo,
  imgTutoriales,
  imgTxtPng,
  imgVariableDataFinal2,
} from "../shared/images";
import { NavBarContainer } from "./NavBarContainer";

export function MarketingHome() {
  const { t } = useTranslation();

  const features = [
    { title: t("marketing.featureNumbers"), img: img123Final2 },
    { title: t("marketing.featureImages"), img: imgImagesFinal },
    { title: t("marketing.featureVariable"), img: imgVariableDataFinal2 },
    { title: t("marketing.featureQr"), img: imgQrCodeNuevo },
    { title: t("marketing.featureText"), img: imgTxtPng },
    { title: t("marketing.featureBarcode"), img: imgBarCodeFinal2 },
  ];

  return (
    <NavBarContainer fullBleed showFooter>
      {/* Hero — brand first, one composition */}
      <section className="marketing-hero">
        <Container size="lg" className="marketing-hero-inner" py="xl">
          <Grid align="center">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="lg" className="marketing-hero-copy">
                <Title
                  order={1}
                  fz={{ base: 42, sm: 52, md: 60 }}
                  lh={1.05}
                  c="charcoal.8"
                >
                  <Text span c="charcoal.5" inherit>
                    Din
                  </Text>
                  <Text span c="brand.6" inherit>
                    Label
                  </Text>
                </Title>
                <Title
                  order={2}
                  fz={{ base: 22, sm: 26 }}
                  fw={500}
                  c="charcoal.7"
                  style={{ fontFamily: "var(--mantine-font-family)" }}
                >
                  {t("marketing.headline")}
                </Title>
                <Text size="lg" c="dimmed" maw={440}>
                  {t("marketing.slogan")}
                </Text>
                <Group gap="md" className="marketing-hero-ctas">
                  <Button
                    component={Link}
                    to="/install"
                    size="lg"
                    color="brand"
                  >
                    {t("nav.download")}
                  </Button>
                  <Button
                    component={Link}
                    to="/buy"
                    size="lg"
                    variant="outline"
                    color="charcoal"
                  >
                    {t("nav.buy")}
                  </Button>
                </Group>
              </Stack>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Box className="marketing-hero-visual" ta="center">
                <Image
                  src={imgDinLabelLaptop}
                  alt="DinLabel"
                  maw={520}
                  mx="auto"
                  style={{
                    filter: "drop-shadow(0 24px 48px rgba(44, 45, 48, 0.18))",
                  }}
                />
              </Box>
            </Grid.Col>
          </Grid>
        </Container>
      </section>

      {/* Features */}
      <Box className="marketing-section" bg="white">
        <Container size="lg">
          <Stack gap="xl">
            <Stack gap="xs" maw={520}>
              <Title order={2} c="charcoal.8">
                {t("marketing.featuresTitle")}
              </Title>
              <Text c="dimmed">{t("marketing.featuresSubtitle")}</Text>
            </Stack>
            <Grid>
              {features.map((f) => (
                <Grid.Col key={f.title} span={{ base: 12, sm: 6, md: 4 }}>
                  <Stack
                    className="marketing-feature"
                    align="center"
                    gap="sm"
                    p="lg"
                    style={{
                      border: "1px solid var(--mantine-color-gray-2)",
                      borderRadius: "var(--mantine-radius-md)",
                      background: "var(--mantine-color-gray-0)",
                      height: "100%",
                    }}
                  >
                    <Image src={f.img} alt={f.title} maw={140} />
                    <Title order={4} ta="center" c="charcoal.8">
                      {f.title}
                    </Title>
                  </Stack>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Container>
      </Box>

      {/* Thermal printers */}
      <Box className="marketing-section marketing-printers" bg="charcoal.8">
        <Container size="lg">
          <Stack gap="lg" maw={720} mx="auto" ta="center">
            <Title order={2} c="white">
              {t("marketing.printersTitle")}
            </Title>
            <Text c="gray.4" size="lg">
              {t("marketing.printersSubtitle")}
            </Text>
            <Text
              className="marketing-printers-brands"
              c="brand.4"
              fw={700}
              fz={{ base: 18, sm: 22 }}
              tt="uppercase"
              style={{ letterSpacing: "0.08em" }}
            >
              {t("marketing.printersBrands")}
            </Text>
            <Text c="gray.5" maw={560} mx="auto">
              {t("marketing.printersNote")}
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Why DinLabel */}
      <Box
        className="marketing-section"
        style={{
          background:
            "linear-gradient(180deg, #fff8f2 0%, #ffffff 100%)",
        }}
      >
        <Container size="lg">
          <Grid align="center">
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Image
                src={imgDinLabelLogoLetras}
                alt="DinLabel"
                maw={300}
                mx="auto"
                radius="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="md">
                <Title order={2} c="charcoal.8">
                  {t("marketing.whyTitle")}
                </Title>
                <Text size="lg" c="dimmed" maw={480}>
                  {t("marketing.whyBody")}
                </Text>
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Try free CTA strip */}
      <Box
        py={56}
        style={{
          background: "var(--mantine-color-charcoal-8)",
        }}
      >
        <Container size="lg">
          <Group
            justify="space-between"
            align="center"
            wrap="wrap"
            gap="lg"
          >
            <div>
              <Title order={2} c="white" mb={6}>
                {t("marketing.tryTitle")}
              </Title>
              <Text c="gray.4" maw={420}>
                {t("marketing.trySubtitle")}
              </Text>
            </div>
            <Button component={Link} to="/install" size="lg" color="brand">
              {t("nav.download")}
            </Button>
          </Group>
        </Container>
      </Box>

      {/* Help */}
      <Box className="marketing-section" bg="white">
        <Container size="lg">
          <Stack gap="xl">
            <Title order={2} ta="center" c="charcoal.8">
              {t("marketing.needHelp")}
            </Title>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack align="center" gap="sm">
                  <Image src={imgMailIcon} alt="" w={56} />
                  <Anchor
                    href="mailto:support@dinlabel.com"
                    fw={600}
                    c="brand.7"
                    size="lg"
                  >
                    {t("marketing.customerService")}
                  </Anchor>
                  <Text ta="center" size="sm" c="dimmed" maw={280}>
                    {t("marketing.customerServiceText")}
                  </Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Stack align="center" gap="sm">
                  <Image src={imgTutoriales} alt="" w={56} />
                  <Anchor
                    component={Link}
                    to="/tutorials"
                    fw={600}
                    c="brand.7"
                    size="lg"
                  >
                    {t("nav.tutorials")}
                  </Anchor>
                  <Text ta="center" size="sm" c="dimmed" maw={280}>
                    {t("marketing.tutorialsText")}
                  </Text>
                </Stack>
              </Grid.Col>
            </Grid>
          </Stack>
        </Container>
      </Box>
    </NavBarContainer>
  );
}
